import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, withModifiers as _withModifiers, Transition as _Transition, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "flex sm:items-center sm:justify-center min-h-screen sm:px-4 sm:pt-4 sm:pb-20 text-center" }
const _hoisted_2 = { class: "inline-block align-bottom sm:align-middle bg-white dark:bg-gray-800 sm:rounded-lg text-left shadow-xl transform transition-all min-h-screen w-full fixed inset-x-0 top-0 sm:static sm:min-h-0 sm:max-w-2xl sm:w-full flex flex-col sm:block overflow-hidden" }
const _hoisted_3 = { class: "bg-gradient-to-tr from-primary to-secondary dark:from-primary-light/10 dark:to-secondary-light/10 px-4 py-4 sm:px-6 flex items-center justify-between shrink-0" }
const _hoisted_4 = { class: "px-4 py-5 sm:p-6 overflow-y-auto flex-1 sm:flex-none max-h-[calc(100vh-10rem)] sm:max-h-none sm:h-[740px]" }
const _hoisted_5 = {
  key: 0,
  class: "flex justify-center py-10"
}
const _hoisted_6 = {
  key: 1,
  class: "space-y-6"
}
const _hoisted_7 = { class: "flex items-center gap-4 pb-4 border-b border-gray-200 dark:border-gray-700" }
const _hoisted_8 = ["src", "alt"]
const _hoisted_9 = { class: "text-xl font-bold text-slate-800 dark:text-white" }
const _hoisted_10 = { class: "text-sm text-slate-500 dark:text-slate-400" }
const _hoisted_11 = { class: "ml-auto" }
const _hoisted_12 = { class: "space-y-6 mt-6" }
const _hoisted_13 = { class: "bg-gray-50 dark:bg-gray-700/30 p-5 rounded-xl border border-gray-200 dark:border-gray-600" }
const _hoisted_14 = { class: "text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 dark:border-gray-600 pb-2" }
const _hoisted_15 = { class: "grid grid-cols-1 md:grid-cols-2 gap-5" }
const _hoisted_16 = { class: "bg-gray-50 dark:bg-gray-700/30 p-5 rounded-xl border border-gray-200 dark:border-gray-600" }
const _hoisted_17 = { class: "text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 dark:border-gray-600 pb-2" }
const _hoisted_18 = { class: "grid grid-cols-1 md:grid-cols-2 gap-5" }
const _hoisted_19 = { class: "md:col-span-2" }
const _hoisted_20 = { class: "bg-gray-50 dark:bg-gray-700/30 p-5 rounded-xl border border-gray-200 dark:border-gray-600" }
const _hoisted_21 = { class: "text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 dark:border-gray-600 pb-2" }
const _hoisted_22 = { class: "grid grid-cols-1 md:grid-cols-2 gap-5" }
const _hoisted_23 = { class: "text-[11px] text-primary/80 mt-1 font-medium" }
const _hoisted_24 = {
  key: 2,
  class: "text-center py-10 text-slate-500"
}
const _hoisted_25 = { class: "bg-gray-50 dark:bg-gray-700 px-4 py-4 flex flex-col gap-3 sm:px-6 sm:flex-row-reverse sm:gap-3 shrink-0" }

import { computed, ref, watch } from 'vue'
import StatusBadge from './StatusBadge.vue'
import DetailField from './DetailField.vue'
import type { Employee } from '@/services/employeeService'
import { Loader2, X, Pencil, User, MapPinHouse, FileUser, Eye, EyeOff } from 'lucide-vue-next'
import api from '@/services/api'


export default /*@__PURE__*/_defineComponent({
  __name: 'EmployeeDetailsModal',
  props: {
    isOpen: { type: Boolean },
    employee: {},
    loading: { type: Boolean }
  },
  emits: ["close", "edit"],
  setup(__props: any) {

const props = __props



const avatarLoadFailed = ref(false)
const showViewPassword = ref(false)

const fullName = computed(() => {
	if (props.employee?.first_name || props.employee?.last_name) {
		return `${props.employee.first_name || ''} ${props.employee.last_name || ''}`.trim()
	}
	return props.employee?.name || 'N/A'
})

const initials = computed(() => {
	const name = fullName.value
	return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
})

const profileImageUrl = computed(() => {
	const raw = String(props.employee?.profile_picture || '').trim()
	if (!raw || raw.toLowerCase() === 'null') return ''
	if (/^https?:\/\//i.test(raw) || raw.startsWith('data:image/') || raw.startsWith('blob:')) return raw
	const rawBase = String(api.defaults.baseURL || '').replace(/\/$/, '')
	const baseNoApi = rawBase.replace(/\/api$/i, '')
	if (raw.startsWith('/storage/')) return `${baseNoApi}${raw}`
	if (raw.startsWith('storage/')) return `${baseNoApi}/${raw}`
	return `${baseNoApi}/storage/${raw.replace(/^\//, '')}`
})

const shouldShowProfileImage = computed(() => Boolean(profileImageUrl.value) && !avatarLoadFailed.value)

watch(profileImageUrl, () => {
  avatarLoadFailed.value = false
})

const onProfileImageError = () => {
  avatarLoadFailed.value = true
}

const avatarBgClass = computed(() => {
	const gradients = [
		'from-primary to-secondary',
		'from-success to-success-hover',
		'from-info to-info-hover',
		'from-warning to-warning-hover',
		'from-danger to-danger-hover',
	]
	const name = fullName.value || 'user'
	let hash = 0
	for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
	return gradients[Math.abs(hash) % gradients.length]
})

const formatDate = (dateStr: string) => {
	if (!dateStr) return 'N/A'
	return new Date(dateStr).toLocaleDateString('en-US', {
		year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit',
	})
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Transition, { name: "modal" }, {
    default: _withCtx(() => [
      (__props.isOpen)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "fixed inset-0 z-50 overflow-y-auto",
            onClick: _cache[5] || (_cache[5] = _withModifiers(($event: any) => (_ctx.$emit('close')), ["self"]))
          }, [
            _createElementVNode("div", _hoisted_1, [
              _createElementVNode("div", {
                class: "fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-opacity-50",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('close')))
              }),
              _createElementVNode("div", _hoisted_2, [
                _createElementVNode("div", _hoisted_3, [
                  _cache[6] || (_cache[6] = _createElementVNode("h3", { class: "text-lg leading-6 font-medium text-white" }, "Employee Details", -1)),
                  _createElementVNode("button", {
                    onClick: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('close'))),
                    class: "text-white/80 hover:text-white transition"
                  }, [
                    _createVNode(_unref(X), { class: "h-5 w-5" })
                  ])
                ]),
                _createElementVNode("div", _hoisted_4, [
                  (__props.loading)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                        _createVNode(_unref(Loader2), { class: "h-8 w-8 animate-spin text-primary" })
                      ]))
                    : (__props.employee)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                          _createElementVNode("div", _hoisted_7, [
                            (shouldShowProfileImage.value)
                              ? (_openBlock(), _createElementBlock("img", {
                                  key: 0,
                                  src: profileImageUrl.value,
                                  alt: `${fullName.value} photo`,
                                  class: "h-16 w-16 rounded-full object-cover shadow-md flex-shrink-0",
                                  onError: onProfileImageError
                                }, null, 40, _hoisted_8))
                              : (_openBlock(), _createElementBlock("div", {
                                  key: 1,
                                  class: _normalizeClass(["h-16 w-16 rounded-full bg-gradient-to-br flex items-center justify-center text-white text-xl font-bold shadow-md flex-shrink-0", avatarBgClass.value])
                                }, _toDisplayString(initials.value), 3)),
                            _createElementVNode("div", null, [
                              _createElementVNode("h2", _hoisted_9, _toDisplayString(fullName.value), 1),
                              _createElementVNode("p", _hoisted_10, _toDisplayString(__props.employee.email), 1)
                            ]),
                            _createElementVNode("div", _hoisted_11, [
                              _createVNode(StatusBadge, {
                                status: __props.employee.status
                              }, null, 8, ["status"])
                            ])
                          ]),
                          _createElementVNode("div", _hoisted_12, [
                            _createElementVNode("div", _hoisted_13, [
                              _createElementVNode("h4", _hoisted_14, [
                                _createVNode(_unref(User), { class: "h-4 w-4 inline" }),
                                _cache[7] || (_cache[7] = _createTextVNode(" Personal Information ", -1))
                              ]),
                              _createElementVNode("div", _hoisted_15, [
                                _createVNode(DetailField, {
                                  label: "First Name",
                                  value: __props.employee.first_name
                                }, null, 8, ["value"]),
                                _createVNode(DetailField, {
                                  label: "Last Name",
                                  value: __props.employee.last_name
                                }, null, 8, ["value"]),
                                _createVNode(DetailField, {
                                  label: "Date of Birth",
                                  value: __props.employee.date_of_birth
                                }, null, 8, ["value"]),
                                _createVNode(DetailField, {
                                  label: "Gender",
                                  value: __props.employee.gender,
                                  capitalize: ""
                                }, null, 8, ["value"]),
                                _createVNode(DetailField, {
                                  label: "Phone",
                                  value: __props.employee.phone
                                }, null, 8, ["value"])
                              ])
                            ]),
                            _createElementVNode("div", _hoisted_16, [
                              _createElementVNode("h4", _hoisted_17, [
                                _createVNode(_unref(MapPinHouse), { class: "h-4 w-4 inline" }),
                                _cache[8] || (_cache[8] = _createTextVNode(" Address ", -1))
                              ]),
                              _createElementVNode("div", _hoisted_18, [
                                _createElementVNode("div", _hoisted_19, [
                                  _createVNode(DetailField, {
                                    label: "Street",
                                    value: __props.employee.address
                                  }, null, 8, ["value"])
                                ]),
                                _createVNode(DetailField, {
                                  label: "City",
                                  value: __props.employee.city
                                }, null, 8, ["value"]),
                                _createVNode(DetailField, {
                                  label: "State",
                                  value: __props.employee.state
                                }, null, 8, ["value"]),
                                _createVNode(DetailField, {
                                  label: "Zipcode",
                                  value: __props.employee.zipcode
                                }, null, 8, ["value"])
                              ])
                            ]),
                            _createElementVNode("div", _hoisted_20, [
                              _createElementVNode("h4", _hoisted_21, [
                                _createVNode(_unref(FileUser), { class: "h-4 w-4 inline" }),
                                _cache[9] || (_cache[9] = _createTextVNode(" Account Information ", -1))
                              ]),
                              _createElementVNode("div", _hoisted_22, [
                                _createVNode(DetailField, {
                                  label: "Position",
                                  value: __props.employee.position || __props.employee.position_id?.toString()
                                }, null, 8, ["value"]),
                                _createVNode(DetailField, {
                                  label: "Email",
                                  value: __props.employee.email
                                }, null, 8, ["value"]),
                                _createVNode(DetailField, {
                                  label: "Username",
                                  value: __props.employee.username
                                }, null, 8, ["value"]),
                                _createElementVNode("div", null, [
                                  _createVNode(DetailField, {
                                    label: "Password",
                                    value: showViewPassword.value ? (__props.employee.visible_password || '') : '********'
                                  }, null, 8, ["value"]),
                                  _createElementVNode("p", _hoisted_23, [
                                    _cache[10] || (_cache[10] = _createTextVNode(" Provided to employee ", -1)),
                                    _createElementVNode("button", {
                                      type: "button",
                                      onClick: _cache[2] || (_cache[2] = ($event: any) => (showViewPassword.value = !showViewPassword.value)),
                                      class: "inline-flex items-center ml-1 text-slate-400 hover:text-primary transition-colors align-middle"
                                    }, [
                                      (_openBlock(), _createBlock(_resolveDynamicComponent(showViewPassword.value ? _unref(EyeOff) : _unref(Eye)), { class: "h-3.5 w-3.5" }))
                                    ])
                                  ])
                                ]),
                                _createVNode(DetailField, {
                                  label: "Date Created",
                                  value: formatDate(__props.employee.created_at)
                                }, null, 8, ["value"]),
                                _createVNode(DetailField, {
                                  label: "Last Updated",
                                  value: formatDate(__props.employee.updated_at)
                                }, null, 8, ["value"])
                              ])
                            ])
                          ])
                        ]))
                      : (_openBlock(), _createElementBlock("div", _hoisted_24, " No employee data available. "))
                ]),
                _createElementVNode("div", _hoisted_25, [
                  _createElementVNode("button", {
                    onClick: _cache[3] || (_cache[3] = ($event: any) => (_ctx.$emit('edit', __props.employee?.id!))),
                    class: "w-full sm:w-auto px-4 py-2 rounded-lg text-sm font-semibold text-white bg-primary hover:bg-secondary transition"
                  }, [
                    _createVNode(_unref(Pencil), { class: "h-4 w-4 inline mr-1" }),
                    _cache[11] || (_cache[11] = _createTextVNode(" Edit ", -1))
                  ]),
                  _createElementVNode("button", {
                    onClick: _cache[4] || (_cache[4] = ($event: any) => (_ctx.$emit('close'))),
                    class: "w-full sm:w-auto px-4 py-2 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 transition"
                  }, " Close ")
                ])
              ])
            ])
          ]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})